import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Spinner, Tabs, Tab, Table, Badge, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  getNombreMes,
  encontrarPrestamoActivo,
  calcularDeudaPendiente as calcularDeudaPendienteDe
} from '../utils/amortizacion';

const ReporteSocioAdmin = () => {
  const { hasPermission } = useAuth();
  const [socios, setSocios] = useState([]);
  const [selectedSocio, setSelectedSocio] = useState(null);
  const [socioDetails, setSocioDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    if (!hasPermission('reports', 'view')) {
      toast.error('No tienes permisos para ver reportes');
      return;
    }
    fetchSocios();
  }, [hasPermission]);

  const fetchSocios = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users?role=socio&limit=1000');
      setSocios(response.data.users || []);
    } catch (error) {
      console.error('Error fetching socios:', error);
      toast.error('Error al cargar socios');
    } finally {
      setLoading(false);
    }
  };

  const fetchSocioDetails = async (socioId) => {
    try {
      setLoadingDetails(true);
      const [socioRes, registrosRes, prestamosRes] = await Promise.all([
        axios.get(`/api/users/${socioId}`),
        axios.get(`/api/registros-mensuales/socio/${socioId}`),
        axios.get(`/api/solicitudes-prestamo/socio/${socioId}`)
      ]);

      setSocioDetails({
        socio: socioRes.data.user,
        registrosMensuales: registrosRes.data.data || [],
        prestamos: prestamosRes.data.data || []
      });
    } catch (error) {
      console.error('Error fetching socio details:', error);
      toast.error('Error al cargar detalles del socio');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSelectSocio = async (socio) => {
    setSelectedSocio(socio);
    await fetchSocioDetails(socio._id);
  };

  const formatCurrency = (num) => {
    return parseFloat(num || 0).toLocaleString('es-ES', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Calcular deuda pendiente basado en cronograma (usa el módulo compartido
  // client/src/utils/amortizacion.js — ver ahí si hace falta corregir algo)
  const calcularDeudaPendiente = () => {
    if (!socioDetails || !socioDetails.prestamos || socioDetails.prestamos.length === 0) {
      return { sinInteres: 0, conInteres: 0 };
    }

    const prestamoActivo = encontrarPrestamoActivo(socioDetails.prestamos);
    if (!prestamoActivo) {
      return { sinInteres: 0, conInteres: 0 };
    }

    return calcularDeudaPendienteDe(prestamoActivo, socioDetails.registrosMensuales || []);
  };

  if (!hasPermission('reports', 'view')) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          No tienes permisos para acceder a esta página.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4">
        <Col>
          <h2>
            <i className="fas fa-user-tie me-2"></i>
            Reporte Individual de Socio
          </h2>
          <p className="text-muted">Selecciona un socio para ver su información completa</p>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <i className="fas fa-search me-2"></i>
                Seleccionar Socio
              </h5>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Buscar Socio</Form.Label>
                <Form.Select
                  value={selectedSocio?._id || ''}
                  onChange={(e) => {
                    const socio = socios.find(s => s._id === e.target.value);
                    if (socio) handleSelectSocio(socio);
                  }}
                >
                  <option value="">-- Selecciona un socio --</option>
                  {socios.map(socio => (
                    <option key={socio._id} value={socio._id}>
                      {socio.name} (Socio #{socio.socioInfo?.numeroSocio || 'N/A'})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {selectedSocio && (
                <Card className="bg-light">
                  <Card.Body>
                    <div className="mb-2">
                      <strong>Nombre:</strong> {selectedSocio.name}
                    </div>
                    <div className="mb-2">
                      <strong>Email:</strong> {selectedSocio.email}
                    </div>
                    <div className="mb-2">
                      <strong>Cédula:</strong> {selectedSocio.cedula || 'N/A'}
                    </div>
                    <div className="mb-2">
                      <strong>Socio #:</strong> {selectedSocio.socioInfo?.numeroSocio || 'N/A'}
                    </div>
                    <div>
                      <strong>Fecha Ingreso:</strong> {new Date(selectedSocio.socioInfo?.fechaIngreso).toLocaleDateString()}
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {loadingDetails ? (
        <Row className="mb-4">
          <Col className="text-center">
            <Spinner animation="border" size="sm" /> Cargando información...
          </Col>
        </Row>
      ) : selectedSocio && socioDetails ? (
        <>
          {/* Tarjetas de resumen */}
          <Row className="mb-4">
            <Col md={3}>
              <Card className="text-center h-100">
                <Card.Body>
                  <i className="fas fa-coins fa-3x text-success mb-3"></i>
                  <h5 className="text-success">Aportaciones</h5>
                  <h4>{formatCurrency(
                    socioDetails.registrosMensuales.reduce((sum, r) => sum + (r.aportes || 0), 0)
                  )}</h4>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center h-100">
                <Card.Body>
                  <i className="fas fa-users fa-3x text-info mb-3"></i>
                  <h5 className="text-info">Paseos</h5>
                  <h4>{formatCurrency(
                    socioDetails.registrosMensuales.reduce((sum, r) => sum + (r.paseo || 0), 0)
                  )}</h4>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center h-100">
                <Card.Body>
                  <i className="fas fa-money-bill fa-3x text-warning mb-3"></i>
                  <h5 className="text-warning">Adeudado (Sin Interés)</h5>
                  <h4>{formatCurrency(calcularDeudaPendiente().sinInteres)}</h4>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center h-100">
                <Card.Body>
                  <i className="fas fa-percent fa-3x text-danger mb-3"></i>
                  <h5 className="text-danger">Adeudado (Con Interés)</h5>
                  <h4>{formatCurrency(calcularDeudaPendiente().conInteres)}</h4>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center h-100">
                <Card.Body>
                  <i className="fas fa-gavel fa-3x text-danger mb-3"></i>
                  <h5 className="text-danger">Multas</h5>
                  <h4>{formatCurrency(
                    socioDetails.registrosMensuales.reduce((sum, r) => sum + (r.multas || 0), 0)
                  )}</h4>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Tabs con detalles */}
          <Row>
            <Col>
              <Card>
                <Card.Body>
                  <Tabs defaultActiveKey="aportaciones" className="mb-3">
                    {/* Pestaña Aportaciones */}
                    <Tab eventKey="aportaciones" title="💰 Aportaciones Mensuales">
                      {socioDetails.registrosMensuales.some(r => r.aportes > 0) ? (
                        <div className="table-responsive mt-3">
                          <Table striped bordered hover size="sm">
                            <thead className="table-primary">
                              <tr>
                                <th>Mes</th>
                                <th>Año</th>
                                <th>Aportación</th>
                              </tr>
                            </thead>
                            <tbody>
                              {socioDetails.registrosMensuales
                                .filter(r => r.aportes > 0)
                                .sort((a, b) => {
                                  if (a.año !== b.año) return b.año - a.año;
                                  return b.mes - a.mes;
                                })
                                .map((registro, idx) => (
                                  <tr key={idx}>
                                    <td><strong>{getNombreMes(registro.mes)}</strong></td>
                                    <td className="text-center">{registro.año}</td>
                                    <td className="text-end text-success">
                                      <strong>{formatCurrency(registro.aportes)}</strong>
                                    </td>
                                  </tr>
                                ))}
                              <tr className="table-light fw-bold">
                                <td colSpan="2">Total Aportaciones:</td>
                                <td className="text-end text-success">
                                  {formatCurrency(
                                    socioDetails.registrosMensuales.reduce((sum, r) => sum + (r.aportes || 0), 0)
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <Alert variant="info" className="mt-3">No hay aportaciones registradas</Alert>
                      )}
                    </Tab>

                    {/* Pestaña Paseos */}
                    <Tab eventKey="paseos" title="👥 Paseos Mensuales">
                      {socioDetails.registrosMensuales.some(r => r.paseo > 0) ? (
                        <div className="table-responsive mt-3">
                          <Table striped bordered hover size="sm">
                            <thead className="table-info">
                              <tr>
                                <th>Mes</th>
                                <th>Año</th>
                                <th>Paseo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {socioDetails.registrosMensuales
                                .filter(r => r.paseo > 0)
                                .sort((a, b) => {
                                  if (a.año !== b.año) return b.año - a.año;
                                  return b.mes - a.mes;
                                })
                                .map((registro, idx) => (
                                  <tr key={idx}>
                                    <td><strong>{getNombreMes(registro.mes)}</strong></td>
                                    <td className="text-center">{registro.año}</td>
                                    <td className="text-end text-info">
                                      <strong>{formatCurrency(registro.paseo)}</strong>
                                    </td>
                                  </tr>
                                ))}
                              <tr className="table-light fw-bold">
                                <td colSpan="2">Total Paseos:</td>
                                <td className="text-end text-info">
                                  {formatCurrency(
                                    socioDetails.registrosMensuales.reduce((sum, r) => sum + (r.paseo || 0), 0)
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <Alert variant="info" className="mt-3">No hay paseos registrados</Alert>
                      )}
                    </Tab>

                    {/* Pestaña Préstamos */}
                    <Tab eventKey="prestamos" title="💵 Préstamos">
                      {socioDetails.prestamos.length > 0 ? (
                        <div className="mt-3">
                          <h6 className="mb-3">Solicitudes de Préstamo</h6>
                          <div className="table-responsive mb-4">
                            <Table striped bordered hover size="sm">
                              <thead className="table-warning">
                                <tr>
                                  <th># Solicitud</th>
                                  <th>Monto</th>
                                  <th>Plazo</th>
                                  <th>Tasa Interés</th>
                                  <th>Interés Total</th>
                                  <th>Estado</th>
                                  <th>Fecha Solicitud</th>
                                </tr>
                              </thead>
                              <tbody>
                                {socioDetails.prestamos
                                  .sort((a, b) => new Date(b.fechaSolicitud) - new Date(a.fechaSolicitud))
                                  .map((prestamo) => (
                                    <tr key={prestamo._id}>
                                      <td className="fw-bold">{prestamo.numeroSolicitud}</td>
                                      <td className="text-end">{formatCurrency(prestamo.monto)}</td>
                                      <td className="text-center">{prestamo.plazo} meses</td>
                                      <td className="text-center">{prestamo.tasaInteres}%</td>
                                      <td className="text-end">{formatCurrency(prestamo.interesTotal)}</td>
                                      <td>
                                        <Badge bg={
                                          prestamo.estado === 'aprobada' ? 'success' :
                                          prestamo.estado === 'pendiente' ? 'warning' :
                                          prestamo.estado === 'rechazada' ? 'danger' :
                                          'info'
                                        }>
                                          {prestamo.estado === 'aprobada' ? 'Aprobada' :
                                           prestamo.estado === 'pendiente' ? 'Pendiente' :
                                           prestamo.estado === 'rechazada' ? 'Rechazada' :
                                           prestamo.estado}
                                        </Badge>
                                      </td>
                                      <td>{new Date(prestamo.fechaSolicitud).toLocaleDateString()}</td>
                                    </tr>
                                  ))}
                              </tbody>
                            </Table>
                          </div>

                          {/* Tabla de Pagos Realizados */}
                          <h6 className="mb-3">Pagos Realizados</h6>
                          {socioDetails.registrosMensuales.some(r => r.capitalPrestamo > 0 || r.interesPrestamo > 0) ? (
                            <div className="table-responsive">
                              <Table striped bordered hover size="sm">
                                <thead className="table-success">
                                  <tr>
                                    <th>Mes</th>
                                    <th>Año</th>
                                    <th>Capital Pagado</th>
                                    <th>Interés Pagado</th>
                                    <th>Total Pago</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {socioDetails.registrosMensuales
                                    .filter(r => r.capitalPrestamo > 0 || r.interesPrestamo > 0)
                                    .sort((a, b) => {
                                      if (a.año !== b.año) return b.año - a.año;
                                      return b.mes - a.mes;
                                    })
                                    .map((registro, idx) => {
                                      const totalPago = (registro.capitalPrestamo || 0) + (registro.interesPrestamo || 0);
                                      return (
                                        <tr key={idx}>
                                          <td><strong>{getNombreMes(registro.mes)}</strong></td>
                                          <td className="text-center">{registro.año}</td>
                                          <td className="text-end text-warning">
                                            {formatCurrency(registro.capitalPrestamo)}
                                          </td>
                                          <td className="text-end text-danger">
                                            {formatCurrency(registro.interesPrestamo)}
                                          </td>
                                          <td className="text-end fw-bold">
                                            {formatCurrency(totalPago)}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  <tr className="table-light fw-bold">
                                    <td colSpan="2">Total Pagado:</td>
                                    <td className="text-end text-warning">
                                      {formatCurrency(
                                        socioDetails.registrosMensuales.reduce((sum, r) => sum + (r.capitalPrestamo || 0), 0)
                                      )}
                                    </td>
                                    <td className="text-end text-danger">
                                      {formatCurrency(
                                        socioDetails.registrosMensuales.reduce((sum, r) => sum + (r.interesPrestamo || 0), 0)
                                      )}
                                    </td>
                                    <td className="text-end">
                                      {formatCurrency(
                                        socioDetails.registrosMensuales.reduce((sum, r) => sum + (r.capitalPrestamo || 0) + (r.interesPrestamo || 0), 0)
                                      )}
                                    </td>
                                  </tr>
                                </tbody>
                              </Table>
                            </div>
                          ) : (
                            <Alert variant="info">No hay pagos registrados</Alert>
                          )}
                        </div>
                      ) : (
                        <Alert variant="info" className="mt-3">No hay préstamos registrados</Alert>
                      )}
                    </Tab>

                    {/* Pestaña Registros Completos */}
                    <Tab eventKey="registros" title="📋 Registros Mensuales Completos">
                      {socioDetails.registrosMensuales.length > 0 ? (
                        <div className="table-responsive mt-3">
                          <Table striped bordered hover size="sm">
                            <thead className="table-secondary">
                              <tr>
                                <th>Mes</th>
                                <th>Año</th>
                                <th>Aportaciones</th>
                                <th>Paseo</th>
                                <th>Capital Préstamo</th>
                                <th>Interés Préstamo</th>
                                <th>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {socioDetails.registrosMensuales
                                .sort((a, b) => {
                                  if (a.año !== b.año) return b.año - a.año;
                                  return b.mes - a.mes;
                                })
                                .map((registro, idx) => {
                                  const total = (registro.aportes || 0) + (registro.paseo || 0) + 
                                               (registro.capitalPrestamo || 0) + (registro.interesPrestamo || 0);
                                  return (
                                    <tr key={idx}>
                                      <td><strong>{getNombreMes(registro.mes)}</strong></td>
                                      <td className="text-center">{registro.año}</td>
                                      <td className="text-end text-success">{formatCurrency(registro.aportes)}</td>
                                      <td className="text-end text-info">{formatCurrency(registro.paseo)}</td>
                                      <td className="text-end text-warning">{formatCurrency(registro.capitalPrestamo)}</td>
                                      <td className="text-end text-danger">{formatCurrency(registro.interesPrestamo)}</td>
                                      <td className="text-end fw-bold">{formatCurrency(total)}</td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <Alert variant="info" className="mt-3">No hay registros mensuales</Alert>
                      )}
                    </Tab>
                  </Tabs>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      ) : selectedSocio && !loadingDetails ? (
        <Row>
          <Col>
            <Alert variant="info">
              Selecciona un socio para ver sus detalles
            </Alert>
          </Col>
        </Row>
      ) : null}
    </Container>
  );
};

export default ReporteSocioAdmin;
